import dot from 'dot-prop-immutable';
import { pathOr } from 'ramda';
import type { AnyAction } from 'redux';
import { REHYDRATE } from 'redux-persist';

import { PAGE_SUCCESS } from 'store/app/actions';

import { formatQuoteId } from 'utils/quoteUtils';
import { getErrorMessage } from 'utils/stringUtils';

import * as types from './types';

const initialState = {
  allExpediteOptionsBuffer: null,
  cancelQuoteFetching: false,
  contactConflict: {
    buttonClicked: null,
    isConflictResolved: false,
    isFetching: false,
    isModalOpen: false,
  },
  copyQuoteFetching: false,
  deleteQuoteFetching: false,
  createRevisionFetching: false,
  digiQuote: null,
  downloadNestPDF: {
    isFetching: false,
  },
  error: null,
  expediteOptionsRowBuffer: null,
  isApplyQuoteExpediteOptionsFetching: false,
  isCancelQuoteConfirmationModalOpen: false,
  isClearQuoteExpediteOptionsFetching: false,
  isExportingQuoteToExcel: false,
  excelExportTasks: [],
  isSendingQuote: false,
  isUpdatingContact: false,
  isUpdatingEstimator: false,
  isUpdatingSalesperson: false,
  isUploading: false,
  quoteEmailTemplates: [],
  quoteId: null,
  quoteNumberWithRevision: null,
  quoteHeaderLastUpdated: null,
  quoteItemInterrogations: {},
  quoteSettingsById: {},
  sendQuote: {
    //TODO - yank out
    resendQuote: false,
    quoteId: null,
    isSending: false,
    error: null,
  },
  sorter: null,
  taxUpdate: {
    isFetching: false,
    revision: null,
  },
  isGenerating: false,
  isBaqInitializing: false,
  isPrivateNotesLoading: false,
  openTaskCount: 0,
  convertingToQuoteItemQuoteFileUuids: [],
  isMarkingQuoteAsLost: false,
  isMarkingQuoteAsSent: false,
};

const reducer = (state = initialState, action: AnyAction = { type: '' }) => {
  const { payload, meta } = action;
  switch (action.type) {
    case PAGE_SUCCESS:
      return {
        ...state,
      };
    case REHYDRATE:
      return {
        ...state,
        quoteSettingsById: pathOr(
          state.quoteSettingsById,
          ['quotes', 'quoteSettingsById'],
          payload,
        ),
      };
    case types.INIT_EDIT_QUOTE:
      return {
        ...state,
        quoteEmailTemplates: [],
        quoteId: payload.id,
        quoteNumberWithRevision: formatQuoteId(payload),
        quoteHeaderLastUpdated: performance.now(),
      };
    case types.QUOTE_RECEIVE:
      return {
        ...state,
        quoteId: payload.id,
        quoteNumberWithRevision: formatQuoteId(payload),
        quoteHeaderLastUpdated: performance.now(),
      };
    case types.QUOTE_CLEAR:
      return {
        ...state,
        quoteId: null,
        quoteNumberWithRevision: null,
        quoteHeaderLastUpdated: null,
      };
    case types.UPLOAD_REQUEST:
      return dot.set(state, 'isUploading', true);
    case types.UPLOAD_SUCCESS:
    case types.UPLOAD_ERROR:
      return dot.set(state, 'isUploading', true);
    case types.EXPORT_TO_EXCEL_REQUEST:
      return {
        ...state,
        isExportingQuoteToExcel: true,
      };
    case types.EXPORT_TO_EXCEL_ERROR:
    case types.EXPORT_TO_EXCEL_SUCCESS:
      return {
        ...state,
        isExportingQuoteToExcel: false,
      };
    case types.ADD_EXCEL_EXPORT_TASKS:
      return {
        ...state,
        excelExportTasks: [...state.excelExportTasks, ...payload.tasks],
      };
    case types.REMOVE_EXCEL_EXPORT_TASKS:
      return {
        ...state,
        excelExportTasks: state.excelExportTasks.filter(
          ({ taskId }) => !payload.taskIds.includes(taskId),
        ),
      };
    case types.RECEIVE_OPEN_TASK_COUNT:
      return {
        ...state,
        openTaskCount: payload.openTaskCount,
      };
    case types.QUOTE_FINALIZE_FLOW:
      return dot.merge(state, 'sendQuote', {
        resendQuote: payload.resendQuote,
      });
    case types.QUOTE_SEND_REQUEST:
      return dot.merge(state, 'sendQuote', {
        error: null,
        isSending: true,
      });
    case types.QUOTE_SEND_SUCCESS:
      return dot.merge(state, 'sendQuote', {
        isSending: false,
      });
    case types.QUOTE_SEND_ERROR:
      return dot.merge(state, 'sendQuote', {
        error: getErrorMessage(payload),
        isSending: false,
      });
    case types.CONTACT_SELECT_ERROR:
    case types.CONTACT_SELECT_SUCCESS:
      return { ...state, isUpdatingContact: false };
    case types.CONTACT_SELECT_REQUEST:
      return { ...state, isUpdatingContact: true };
    case types.ESTIMATOR_SELECT_ERROR:
    case types.ESTIMATOR_SELECT_SUCCESS:
      return { ...state, isUpdatingEstimator: false };
    case types.ESTIMATOR_SELECT_REQUEST:
      return { ...state, isUpdatingEstimator: true };
    case types.SALESPERSON_SELECT_ERROR:
    case types.SALESPERSON_SELECT_SUCCESS:
      return { ...state, isUpdatingSalesperson: false };
    case types.SALESPERSON_SELECT_REQUEST:
      return { ...state, isUpdatingSalesperson: true };
    case types.SUPPLIER_FACILITY_SELECT_ERROR:
    case types.SUPPLIER_FACILITY_SELECT_SUCCESS:
      return { ...state, isUpdatingSupplierFacility: false };
    case types.SUPPLIER_FACILITY_SELECT_REQUEST:
      return { ...state, isUpdatingSupplierFacility: true };
    case types.CHANGE_QUOTE_PRIORITY_ERROR:
    case types.CHANGE_QUOTE_PRIORITY_SUCCESS:
      return { ...state, isUpdatingQuotePriority: false };
    case types.CHANGE_QUOTE_PRIORITY_REQUEST:
      return { ...state, isUpdatingQuotePriority: true };
    case types.SAVE_QUOTE_SETTINGS_BY_ID:
      return {
        ...state,
        quoteSettingsById: {
          ...state.quoteSettingsById,
          [payload.quoteWithSettingsId]: payload.quoteSettings,
        },
      };
    case types.OPEN_CANCEL_QUOTE_CONFIRMATION_MODAL:
      return {
        ...state,
        isCancelQuoteConfirmationModalOpen: true,
        cancelQuoteFetching: true,
      };
    case types.CONFIRM_CANCEL_QUOTE:
      return { ...state, isCancelQuoteConfirmationModalOpen: false };
    case types.CLOSE_CANCEL_QUOTE_CONFIRMATION_MODAL:
      return {
        ...state,
        isCancelQuoteConfirmationModalOpen: false,
        cancelQuoteFetching: false,
      };
    case types.QUOTE_CREATE_REVISION_SUCCESS:
    case types.QUOTE_CREATE_REVISION_ERROR:
      return { ...state, createRevisionFetching: false };
    case types.QUOTE_CREATE_REVISION_REQUEST:
      return { ...state, createRevisionFetching: true };
    case types.QUOTE_CANCEL_SUCCESS:
    case types.QUOTE_CANCEL_ERROR:
      return { ...state, cancelQuoteFetching: false };
    case types.QUOTE_COPY_REQUEST:
      return { ...state, copyQuoteFetching: true };
    case types.QUOTE_COPY_SUCCESS:
    case types.QUOTE_COPY_ERROR:
      return { ...state, copyQuoteFetching: false };
    case types.QUOTE_DELETE_SUCCESS:
    case types.QUOTE_DELETE_ERROR:
      return { ...state, deleteQuoteFetching: false };
    case types.QUOTE_DELETE_REQUEST:
      return { ...state, deleteQuoteFetching: true };
    case types.FETCH_EMAIL_TEMPLATES_SUCCESS:
      return {
        ...state,
        quoteEmailTemplates: payload.emailTemplates,
      };
    case types.COPY_EXPEDITE_OPTIONS_ROW:
      return {
        ...state,
        expediteOptionsRowBuffer: payload,
      };
    case types.COPY_ALL_EXPEDITE_OPTIONS:
      return {
        ...state,
        allExpediteOptionsBuffer: payload,
      };
    case types.APPLY_QUOTE_EXPEDITE_OPTIONS_REQUEST:
      return {
        ...state,
        isApplyQuoteExpediteOptionsFetching: true,
      };
    case types.APPLY_QUOTE_EXPEDITE_OPTIONS_SUCCESS:
    case types.APPLY_QUOTE_EXPEDITE_OPTIONS_ERROR:
      return {
        ...state,
        isApplyQuoteExpediteOptionsFetching: false,
      };
    case types.CLEAR_QUOTE_EXPEDITE_OPTIONS_REQUEST:
      return {
        ...state,
        isClearQuoteExpediteOptionsFetching: true,
      };
    case types.CLEAR_QUOTE_EXPEDITE_OPTIONS_SUCCESS:
    case types.CLEAR_QUOTE_EXPEDITE_OPTIONS_ERROR:
      return {
        ...state,
        isClearQuoteExpediteOptionsFetching: false,
      };
    case types.APPLY_EXPEDITE_OPTIONS_ROW_TO_ALL:
    case types.PASTE_EXPEDITE_OPTIONS_ROW:
    case types.PASTE_ALL_EXPEDITE_OPTIONS:
    case types.CLEAR_EXPEDITE_OPTIONS_ROW:
    case types.CLEAR_ALL_EXPEDITE_OPTIONS:
      return {
        ...state,
        isExpediteOptionsUpdating: true,
      };
    case types.FINISH_UPDATING_EXPEDITE_OPTIONS:
      return {
        ...state,
        isExpediteOptionsUpdating: false,
      };
    case types.CONTACT_CONFLICT_OPEN_MODAL:
      return dot.merge(state, 'contactConflict', {
        isConflictResolved: false,
        isModalOpen: true,
      });
    case types.CONTACT_CONFLICT_CLOSE_MODAL:
      return dot.merge(state, 'contactConflict', {
        isConflictResolved: false,
        isModalOpen: false,
      });
    case types.CONTACT_CONFLICT_RESOLVE:
      return dot.merge(state, 'contactConflict', {
        isFetching: true,
        buttonClicked: payload.buttonClicked,
      });
    case types.CONTACT_CONFLICT_SUCCESS:
      return dot.merge(state, 'contactConflict', {
        isConflictResolved: true,
        isFetching: false,
      });
    case types.CONTACT_CONFLICT_ERROR:
      return dot.merge(state, 'contactConflict', {
        isFetching: false,
      });
    case types.DOWNLOAD_NEST_PDF_REQUEST:
      return dot.merge(state, 'downloadNestPDF', {
        isFetching: true,
      });
    case types.DOWNLOAD_NEST_PDF_SUCCESS:
    case types.DOWNLOAD_NEST_PDF_ERROR:
      return dot.merge(state, 'downloadNestPDF', {
        isFetching: false,
      });
    case types.CHANGE_TAX_REQUEST:
      return dot.set(state, 'taxUpdate.isFetching', true);
    case types.CHANGE_TAX_SUCCESS:
      return dot.set(state, 'taxUpdate.isFetching', false);
    case types.CHANGE_TAX_ERROR:
      return dot.merge(state, 'taxUpdate', {
        isFetching: false,
        revision: meta.revision,
      });
    case types.BAQ_PAGE_INIT_REQUEST:
      return {
        ...state,
        isBaqInitializing: true,
      };
    case types.BAQ_PAGE_INIT_SUCCESS:
      return {
        ...state,
        isBaqInitializing: false,
      };
    case types.BAQ_PAGE_INIT_ERROR:
      return {
        ...state,
        isBaqInitializing: false,
      };
    case types.QUOTES_PRIVATE_NOTES_UPDATE:
      return dot.set(state, 'isPrivateNotesLoading', true);
    case types.QUOTES_PRIVATE_NOTES_UPDATE_SUCCESS:
    case types.QUOTES_PRIVATE_NOTES_UPDATE_ERROR:
      return dot.set(state, 'isPrivateNotesLoading', false);
    case types.CONVERT_QUOTE_FILES_TO_LINE_ITEMS_REQUEST:
      return {
        ...state,
        convertingToQuoteItemQuoteFileUuids: [
          ...new Set([
            ...state.convertingToQuoteItemQuoteFileUuids,
            ...payload.fileUuids,
          ]),
        ],
      };
    case types.CONVERT_QUOTE_FILES_TO_LINE_ITEMS_FINISH:
      return {
        ...state,
        convertingToQuoteItemQuoteFileUuids:
          state.convertingToQuoteItemQuoteFileUuids.filter(
            (sf) => !payload.fileUuids.includes(sf),
          ),
      };
    case types.QUOTE_MARK_LOST_REQUEST:
      return { ...state, isMarkingQuoteAsLost: true };
    case types.QUOTE_MARK_LOST_ERROR:
    case types.QUOTE_MARK_LOST_SUCCESS:
      return { ...state, isMarkingQuoteAsLost: false };
    case types.QUOTE_MARK_SENT_REQUEST:
      return { ...state, isMarkingQuoteAsSent: true };
    case types.QUOTE_MARK_SENT_ERROR:
    case types.QUOTE_MARK_SENT_SUCCESS:
      return { ...state, isMarkingQuoteAsSent: false };
    default:
      return state;
  }
};

export default reducer;
